import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from '@dropbox/dig-components/buttons';
import { Text, Title } from '@dropbox/dig-components/typography';
import { getBrandName } from 'js/sign-components/common/brand';
import { useSiteCode } from 'js/sign-components/common/use-site-code';
import { FaxPlanHighlights } from 'hellospa/components/fax-plan-highlights';
import { PlanMonthlyRateDisplay } from 'hellosign/components/pricing/plan-monthly-rate-display';
import { TopBarLayout } from 'hellosign/common/layout';
import { redirectTo } from 'hellospa/common/utils/redirect';
import { useUnsafePageProps } from 'hellospa/slices/page-props';

import type { OnboardingUpsellPageProps } from './types';
import styles from './index.module.css';

function OnboardingUpsellContent({
  pricingUrl,
  skipUrl,
  formattedMonthlyRate,
  formattedCouponMonthlyPrice,
  couponNumMonths,
  hasValidCoupon,
  planName,
  numFaxPages,
  numSeatsMax,
}: OnboardingUpsellPageProps) {
  const siteCode = useSiteCode();
  const brandName = getBrandName(siteCode);

  return (
    <div className={styles.container} data-testid="onboarding-upsell-page">
      <Title size="medium">
        <FormattedMessage
          id="onboardingUpsell.title"
          description="Title on the fax onboarding upsell page encouraging users to upgrade for a fax number"
          defaultMessage="Get your own fax number"
        />
      </Title>

      <Text variant="paragraph">
        <FormattedMessage
          id="onboardingUpsell.sendOnlyBody"
          description="Body copy on the onboarding upsell page explaining that free accounts can only send faxes"
          defaultMessage="With your current account, you can send faxes. Upgrade to also receive faxes on your own dedicated number—we'll email incoming faxes to you as PDFs."
        />
      </Text>

      {formattedMonthlyRate && (
        <div
          className={styles.pricingCard}
          data-testid="onboarding-upsell-pricing"
        >
          {planName && (
            <Title size="small" className={styles.planName}>
              {planName}
            </Title>
          )}
          <PlanMonthlyRateDisplay
            className={styles.pricingRate}
            formattedMonthlyRate={formattedMonthlyRate}
            formattedCouponMonthlyPrice={formattedCouponMonthlyPrice}
            couponNumMonths={couponNumMonths}
            hasValidCoupon={hasValidCoupon}
            size="large"
            tagName="div"
          />
        </div>
      )}

      <FaxPlanHighlights
        className={styles.highlights}
        rowClassName={styles.highlightRow}
        plan={{
          num_fax_pages: numFaxPages,
          num_seats_max: numSeatsMax,
          name: planName,
        }}
      />

      <Text variant="paragraph" color="subtle">
        <FormattedMessage
          id="onboardingUpsell.footerNote"
          description="Supporting copy on the onboarding upsell page about the {brandName} service"
          defaultMessage="{brandName} makes it easy to ditch your fax machine and manage faxes from anywhere."
          values={{ brandName }}
        />
      </Text>

      <div className={styles.actions}>
        <Button
          variant="primary"
          size="large"
          onClick={() => redirectTo(pricingUrl)}
          data-testid="onboarding-upsell-cta"
        >
          <FormattedMessage
            id="onboardingUpsell.cta"
            description="Primary button on the onboarding upsell page that starts Home Office checkout"
            defaultMessage="Upgrade to Home Office"
          />
        </Button>
        <Button
          variant="opacity"
          size="large"
          onClick={() => redirectTo(skipUrl)}
          data-testid="onboarding-upsell-skip"
        >
          <FormattedMessage
            id="onboardingUpsell.skip"
            description="Skip button on the onboarding upsell page for users who do not want to upgrade"
            defaultMessage="No thanks, I'm good"
          />
        </Button>
      </div>
    </div>
  );
}

const OnboardingUpsellPage = () => {
  const pageProps = useUnsafePageProps() as OnboardingUpsellPageProps;

  return (
    <TopBarLayout>
      <div className={styles.page}>
        <OnboardingUpsellContent {...pageProps} />
      </div>
    </TopBarLayout>
  );
};

export default OnboardingUpsellPage;
